import { UseChatHelpers } from '@ai-sdk/react';
import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { createContext, useContext, useMemo } from 'react';

import { Metadata, UIChatMessage } from '../Chat/chat.types.ts';
import { getChatUserMessageSubmit as getUserMessageFormatter } from '../Chat/ChatUserMessage/ChatUserMessage.utils.ts';
import { useFrontdeskAssistant } from '@/legacy/hooks/useFrontdeskAssistant/useFrontdeskAssistant.ts';
import { RESET_CONVERSATION_MUTATION } from '~/components/ConversationContextProvider/ResetConversation.gql.ts';
import { useAgentSubmitHandler } from '~/routes/IntakeChat/hooks/useAgentSubmitHandler.ts';

import { ConversationContextValue } from './ConversationContextProvider.types.ts';
import { CONVERSATION_QUERY } from './QueryConversation.gql.ts';

export const ConversationContext = createContext<ConversationContextValue | null>(null);

export const useConversationContext = () => {
  const context = useContext(ConversationContext);
  if (context == null) {
    throw new Error('useConversationContext must be used within a ConversationContextProvider');
  }
  return context;
};

export const useAgentSubmitHandlerFromContext = () => {
  const context = useConversationContext();
  const agentId = useFrontdeskAssistant();

  return useAgentSubmitHandler(agentId, context.sendMessage);
};

export const useQuickActions = () => {
  const { quickActions, sendMessage } = useConversationContext();
  const onSubmit = getConversationMessageSubmit({ sendMessage });
  return {
    quickActions,
    onSubmit,
  };
};

interface UseConversationMessageSubmitProps {
  sendMessage: UseChatHelpers<UIChatMessage>['sendMessage'];
}

export const getConversationMessageSubmit = ({ sendMessage }: UseConversationMessageSubmitProps) => {
  return async (args: { text: string }, metadata?: Metadata) => {
    const formatMessage = getUserMessageFormatter(metadata?.type);
    const message = formatMessage(args, metadata);
    sendMessage(message);
  };
};

export const useInitializeConversation = ({ entityId, entityVariant, title, skip }: any) => {
  const { onError } = useCustomSnackbar();
  const variables = useMemo(
    () => ({
      entityId,
      entityVariant,
      description: title,
    }),
    [entityId, entityVariant, title],
  );
  const { data: conversationData, loading: querying } = useQuery(CONVERSATION_QUERY, {
    variables,
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const {
    id: conversationId,
    conversationSettings,
    conversationMessages = [],
  } = conversationData?.sevenbridges.conversation ?? {};
  const { quickActions = [] } = conversationSettings ?? {};
  const messageHistory = (conversationMessages ?? []) as unknown as UIChatMessage[];

  const [resetConversation, { loading: resetting }] = useMutation(RESET_CONVERSATION_MUTATION, {
    variables: { data: { id: conversationId } },
    onError,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: CONVERSATION_QUERY,
        variables,
      },
    ],
  });

  return {
    conversationId,
    quickActions,
    messageHistory,
    resetConversation,
    loading: skip || querying || resetting,
  };
};
